/**
 * LocalGameServer - Embedded server for offline/local play
 *
 * This provides the same functionality as the remote Colyseus server
 * but runs entirely in the client, allowing offline single-player.
 */

import {
    WorldData,
    PlayerState,
    TileType,
    TICK_INTERVAL,
    HP_DEFAULT,
    SlashData,
    PortalAddress,
    CHUNK_SIZE,
    GameSimulation,
    SimulationEvent,
    MouseSimulation,
    MouseEvent,
    MOUSE_SPAWN_PHRASE,
    RAT_SPAWN_PHRASE,
    SQUIRREL_SPAWN_PHRASE,
    CRAB_SPAWN_PHRASE,
    RodentType,
    matchCreatureSpawn,
    PLAYER_HEIGHT_STANDING,
    PLAYER_HEIGHT_CROUCHING,
    BLOCK_SPAWN_COMMANDS,
    JUMP_HEIGHT,
    JUMP_HEIGHT_COMMANDS,
    JUMP_HEIGHT_OTHER_COMMANDS,
    TREE_SPAWN_PHRASE,
    buildTreeBlocks,
    RATS_NEST_TIMER,
    computeNestRespawnPosition,
    MAX_AIR_TICKS,
    isEquipmentItem,
    findDefaultSpawnPosition,
    isBoneLikeBlock,
    isIndestructible,
    isShrubTile,
    isFruitTile,
    type ToolActionType,
    isHealingFruit,
    getFruitHealAmount,
    KILI_KULUPU_HEAL_TOTAL,
    KILI_NIGHTSHADE_DAMAGE,
    isSandTile,
    getCreatureSize,
} from '@blockworld/shared';
import { PlayerSnapshot, ConnectionStatus } from './NetworkClient';

// ============================================================
// LOCAL PLAYER DATA
// ============================================================

interface LocalPlayerData {
    id: string;
    x: number;
    y: number;
    state: PlayerState;
    facing: number;
    isCrouching: boolean;
    isHoldingBlock: boolean;
    heldBlockType: number;
    hp: number;
    maxHp: number;
    isHoldingSword: boolean;
    swordHoldStartTick: number;
    runeStrikeTicks: Map<string, number>;  // "x,y" -> tick when last struck (3-tick cooldown)
    lastChopTick: number;
    lastSwordDestroyTick: number;
    jumpHeight: number;
    airTicks: number;
    drowningTickCounter: number;
    spawnTime: number;
    wasInPortal: boolean;
    wantsPortalActivation: boolean;
    equippedItem: number;
    hasBoots: boolean;
    bucketFull: boolean;
    basketContents: number[];
    isGliding: boolean;
    glideDir: number;
    spawnBlockX: number;
    spawnBlockY: number;
    thornbushTickCounter: number;
    fruitHealRemaining: number;
    fruitHealTickCounter: number;
    kiliLawaTickCounter: number;
    suffocationTickCounter: number;
}

// ============================================================
// LOCAL GAME SERVER
// ============================================================

export class LocalGameServer {
    worldData: WorldData;
    private player: LocalPlayerData;
    private seed: number;
    private tickInterval: NodeJS.Timeout | null = null;
    private lastGlobalTick: number = 0;
    private simulation: GameSimulation;
    private mouseSimulation: MouseSimulation = new MouseSimulation();
    private logInertia: Map<string, number> = new Map(); // "x,y" -> facing dir for LOG rolling

    // Callbacks (same interface as NetworkClient)
    onConnectionChange: ((status: ConnectionStatus) => void) | null = null;
    onChunkData: ((chunks: { cx: number; cy: number; tiles: number[][] }[]) => void) | null = null;
    onBlockUpdate: ((updates: {
        x: number;
        y: number;
        tileType: number;
        action: string;
        playerId?: string;
    }[]) => void) | null = null;
    onPlayerPositions: ((snapshots: PlayerSnapshot[]) => void) | null = null;
    onInitialState: ((localId: string, seed: number, players: PlayerSnapshot[]) => void) | null = null;
    onSlash: ((slash: SlashData) => void) | null = null;
    onDamage: ((playerId: string, hp: number, source: 'sword' | 'slash' | 'mouse' | 'drowning') => void) | null = null;
    onMaxHpUpdate: ((maxHp: number, hp: number) => void) | null = null;
    onDeath: ((playerId: string) => void) | null = null;
    onRuneUpdate: ((x: number, y: number, hieroglyphIndex: number) => void) | null = null;
    onTeleport: ((destination: PortalAddress, portalCode: number[]) => void) | null = null;
    onSpeech: ((playerId: string, words: number[], voice: string) => void) | null = null;
    onMouseSpawn: ((id: number, x: number, y: number, facing: number, rodentType: string, wallDir?: number, vertDir?: number) => void) | null = null;
    onMouseDeath: ((id: number) => void) | null = null;
    onBlockSpawn: ((blockType: TileType) => void) | null = null;
    onJumpHeightUpdate: ((jumpHeight: number) => void) | null = null;
    onYellowKeyPickup: (() => void) | null = null;
    onAirUpdate: ((airTicks: number) => void) | null = null;
    onPressurePlateChanged: ((x: number, y: number, active: boolean) => void) | null = null;
    onSignUpdate: ((x: number, y: number, glyphIndex: number) => void) | null = null;
    onEyeSwitchActivated: ((x: number, y: number, tick: number) => void) | null = null;
    onSpawnPointSet: ((x: number, y: number) => void) | null = null;
    onRespawnAt: ((x: number, y: number) => void) | null = null;

    constructor(seed?: number) {
        this.seed = seed || Date.now();
        this.worldData = new WorldData(this.seed);
        this.simulation = new GameSimulation(this.worldData);

        // Spawn in the underground cavern
        const spawn = findDefaultSpawnPosition();

        this.player = {
            id: 'local',
            x: spawn.x,
            y: spawn.y,
            state: PlayerState.IDLE,  // Start standing, not falling
            facing: 1,
            isCrouching: false,
            isHoldingBlock: false,
            heldBlockType: -1,
            hp: HP_DEFAULT,
            maxHp: HP_DEFAULT,
            isHoldingSword: false,
            swordHoldStartTick: 0,
            runeStrikeTicks: new Map(),
            lastChopTick: -999,
            lastSwordDestroyTick: -999,
            jumpHeight: 0,
            airTicks: MAX_AIR_TICKS,
            drowningTickCounter: 0,
            spawnTime: Date.now(),
            wasInPortal: false,
            wantsPortalActivation: false,
            equippedItem: -1,
            hasBoots: false,
            bucketFull: false,
            basketContents: [],
            isGliding: false,
            glideDir: 1,
            spawnBlockX: -1,
            spawnBlockY: -1,
            thornbushTickCounter: 0,
            fruitHealRemaining: 0,
            fruitHealTickCounter: 0,
            kiliLawaTickCounter: 0,
            suffocationTickCounter: 0,
        };
    }

    /**
     * Start the local server (simulates connection)
     */
    async connect(): Promise<void> {
        console.log('[LOCAL] connect() called');
        this.onConnectionChange?.('connecting');

        // Small delay to simulate connection
        await new Promise(resolve => setTimeout(resolve, 100));

        // Send initial state
        const playerSnapshot: PlayerSnapshot = {
            id: this.player.id,
            x: this.player.x,
            y: this.player.y,
            state: this.player.state,
            facing: this.player.facing,
            isCrouching: this.player.isCrouching,
            isHoldingBlock: this.player.isHoldingBlock,
            heldBlockType: this.player.heldBlockType,
            hp: this.player.hp,
            maxHp: this.player.maxHp,
            isHoldingSword: this.player.isHoldingSword,
            hasBoots: this.player.hasBoots,
            timestamp: Date.now(),
            sequence: 0,
        };

        console.log('[LOCAL] Firing onInitialState callback, callback exists:', !!this.onInitialState);
        this.onInitialState?.(this.player.id, this.seed, [playerSnapshot]);

        // Notify scene of restored jump height (onInitialState creates localPlayer with jumpHeight=0)
        if (this.player.jumpHeight > 0) {
            this.onJumpHeightUpdate?.(this.player.jumpHeight);
        }

        // Start tick loop
        this.lastGlobalTick = Math.floor(Date.now() / TICK_INTERVAL);
        this.tickInterval = setInterval(() => {
            const currentTick = Math.floor(Date.now() / TICK_INTERVAL);
            if (currentTick > this.lastGlobalTick) {
                this.lastGlobalTick = currentTick;
                // Falling blocks are handled by client prediction (GameScene.tickLocalFallingBlocks)
                // which shares the same tile data. Running a second physics engine here causes
                // duplication when the two race on the same shared mutable chunk arrays.
                this.tickMice();
                this.tickNests();
                this.tickCombat(currentTick);
                this.tickClubMelee(currentTick);
                this.tickBreathing();
                this.tickBoots();
                this.tickYellowKey();
                this.tickPortals();
                this.tickPressurePlates();
                this.tickCommandBlocks(currentTick);
                this.tickThwomps();
                this.tickQuestionBlocks(currentTick);
                this.tickThornbushes();
                this.tickShrubSupport();
                this.tickFruitHazards();
                this.tickSlowHeal();
                this.tickSuffocation();
            }
        }, 10);

        this.onConnectionChange?.('connected');
        console.log('[LOCAL] connect() complete');
    }

    /**
     * Stop the local server
     */
    disconnect(): void {
        if (this.tickInterval) {
            clearInterval(this.tickInterval);
            this.tickInterval = null;
        }
        this.onConnectionChange?.('disconnected');
    }

    /**
     * Request chunks around a position
     */
    subscribeToChunks(chunks: { cx: number; cy: number }[]): void {
        const chunkData: { cx: number; cy: number; tiles: number[][] }[] = [];

        for (const { cx, cy } of chunks) {
            const tiles = this.worldData.getChunk(cx, cy);
            chunkData.push({ cx, cy, tiles });
        }

        this.onChunkData?.(chunkData);
    }

    /**
     * Handle position update from client
     */
    sendPosition(
        x: number,
        y: number,
        state: PlayerState,
        facing: number,
        isCrouching: boolean,
        isHoldingBlock: boolean,
        heldBlockType: TileType | null
    ): void {
        this.player.x = x;
        this.player.y = y;
        this.player.state = state;
        this.player.facing = facing;
        this.player.isCrouching = isCrouching;
        this.player.isHoldingBlock = isHoldingBlock;
        this.player.heldBlockType = heldBlockType ?? -1;
    }

    /**
     * Handle block action from client
     */
    sendBlockAction(
        action: 'pickup' | 'place' | 'combine' | 'swap',
        x: number,
        y: number,
        blockType: TileType,
        playerX: number,
        playerY: number,
        playerState: PlayerState,
        playerFacing: number,
        playerIsCrouching: boolean,
        playerIsHoldingBlock: boolean,
        playerHeldBlockType: number
    ): void {
        // Update player state
        this.player.x = playerX;
        this.player.y = playerY;
        this.player.state = playerState;
        this.player.facing = playerFacing;
        this.player.isCrouching = playerIsCrouching;
        this.player.isHoldingBlock = playerIsHoldingBlock;
        this.player.heldBlockType = playerHeldBlockType;

        // Apply block change
        // Placing KILI_MA (potato) onto DIRT → KASI_MA
        if (action === 'place' && blockType === TileType.KILI_MA) {
            const targetTile = this.worldData.getTile(x, y);
            if (targetTile === TileType.KASI_MA) {
                // Client already converted dirt→kasi_ma. Done.
                return;
            }
            // Fall through to normal placement if no dirt was found
        }

        if (action === 'place' && isBoneLikeBlock(blockType)) {
            // In singleplayer, GameScene and LocalGameServer share the same worldData.
            // The client prediction has ALREADY mutated the tile before this runs:
            //   - Bone-like + Stick: client set tile to SAPLING (stick→sapling conversion done)
            //   - Bone-like + Sapling: client set tile to SKY (sapling cleared, tree needed)
            //   - Normal placement: client set tile to block (already placed)
            const targetTile = this.worldData.getTile(x, y);
            if (targetTile === TileType.SAPLING) {
                // Client already converted stick→sapling. Nothing more to do.
                return;
            }
            if (targetTile === TileType.SKY) {
                // Client cleared the sapling — spawn tree at that position.
                this.spawnTreeAtPosition(x, y);
                return;
            }
            // Normal placement — client already set the tile. Fall through to broadcast.
        } else if (action === 'place') {
            this.worldData.setTile(x, y, blockType);
            // Track LOG inertia from player facing direction
            if (blockType === TileType.LOG) {
                this.logInertia.set(`${x},${y}`, playerFacing);
            }
            // Dice: roll on placement
            if (blockType === TileType.ILO_ANU) {
                this.worldData.rollDiceAt(x, y);
            }
            // Sign: init glyph data on placement
            if (blockType === TileType.KIWEN_NIMI) {
                this.worldData.setSignGlyph(x, y, 0);
            }
            // Spawn block: set spawn point on placement
            if (blockType === TileType.KIWEN_MI) {
                this.player.spawnBlockX = x;
                this.player.spawnBlockY = y - 1;
                this.onSpawnPointSet?.(x, y - 1);
            }
        } else if (action === 'combine') {
            // STICK-on-STICK: delete the target stick, player now holds LADDER
            this.worldData.setTile(x, y, TileType.SKY);
            this.worldData.removeFallingBlock(x, y);
            this.worldData.propagateInstabilityUp(x, y - 1);
        } else if (action === 'swap') {
            // Equipment swap: client already placed old item and removed new item
            // Just update server-side equippedItem tracking
            this.player.equippedItem = Number(blockType);
        } else {
            // Indestructible blocks cannot be picked up
            if (isIndestructible(blockType)) {
                return;
            }
            // Shrub: just destroy, don't pick up
            if (isShrubTile(blockType)) {
                this.worldData.setTile(x, y, TileType.SKY);
                this.player.isHoldingBlock = false;
                this.player.heldBlockType = -1;
                return;
            }
            // KASI_MA (Potato in Dirt): separate into dirt + potato
            if (blockType === TileType.KASI_MA) {
                this.worldData.setTile(x, y, TileType.DIRT);
                this.worldData.addFallingBlock(x, y);
                this.player.isHoldingBlock = true;
                this.player.heldBlockType = TileType.KILI_MA;
                return;
            }
            // KIWEN_MI: unpickupable — set spawn point instead
            if (blockType === TileType.KIWEN_MI) {
                this.player.spawnBlockX = x;
                this.player.spawnBlockY = y - 1;
                this.onSpawnPointSet?.(x, y - 1);
                return;
            }
            // KIWEN_NIMI: clean up sign data on pickup
            if (blockType === TileType.KIWEN_NIMI) {
                this.worldData.removeSignData(x, y);
            }
            // Flint + Stick in hand → Club (ILO_IJO) crafting
            if (blockType === TileType.FLINT && playerIsHoldingBlock && playerHeldBlockType === TileType.STICK) {
                this.worldData.setTile(x, y, TileType.SKY);
                this.worldData.removeFallingBlock(x, y);
                this.player.isHoldingBlock = false;
                this.player.heldBlockType = -1;
                this.player.equippedItem = TileType.ILO_IJO;
                return;
            }
            this.worldData.setTile(x, y, TileType.SKY);
            this.worldData.removeFallingBlock(x, y);
            this.worldData.propagateInstabilityUp(x, y - 1);
            // Heart container: consume and heal, or hold above head if full HP
            if (blockType === TileType.HEART_CONTAINER) {
                if (this.player.hp < this.player.maxHp) {
                    this.player.isHoldingBlock = false;
                    this.player.heldBlockType = -1;
                    this.player.hp = Math.min(this.player.hp + 1, this.player.maxHp);
                    this.onDamage?.(this.player.id, this.player.hp, 'sword');
                }
                // At full HP: keep held above head
            }
            // Healing fruits: consume and heal, or hold above head if full HP
            if (isHealingFruit(blockType) && blockType !== TileType.KILI_KULUPU) {
                if (this.player.hp < this.player.maxHp) {
                    this.player.isHoldingBlock = false;
                    this.player.heldBlockType = -1;
                    const healAmount = getFruitHealAmount(blockType);
                    this.player.hp = Math.min(this.player.hp + healAmount, this.player.maxHp);
                    this.onDamage?.(this.player.id, this.player.hp, 'sword');
                }
            }
            // Grape cluster: start slow heal or hold if full
            if (blockType === TileType.KILI_KULUPU) {
                if (this.player.hp < this.player.maxHp) {
                    this.player.isHoldingBlock = false;
                    this.player.heldBlockType = -1;
                    this.player.fruitHealRemaining = KILI_KULUPU_HEAL_TOTAL;
                    this.player.fruitHealTickCounter = 0;
                }
            }
            // Nightshade: always consume and damage
            if (blockType === TileType.KILI_IKE) {
                this.player.isHoldingBlock = false;
                this.player.heldBlockType = -1;
                this.player.hp = Math.max(this.player.hp - KILI_NIGHTSHADE_DAMAGE, 0);
                this.onDamage?.(this.player.id, this.player.hp, 'sword');
            }
            // Big heart container: consume, increase max HP, fully heal
            if (blockType === TileType.BIG_HEART_CONTAINER) {
                this.player.isHoldingBlock = false;
                this.player.heldBlockType = -1;
                this.player.maxHp += 1;
                this.player.hp = this.player.maxHp;
                this.onMaxHpUpdate?.(this.player.maxHp, this.player.hp);
            }
            // Equipment items go into equipment slot
            if (isEquipmentItem(blockType)) {
                this.player.equippedItem = Number(blockType);
            }
        }

        // Broadcast update (include playerId so client recognises this as its own echo)
        const tileType = action === 'place' ? blockType : TileType.SKY;
        this.onBlockUpdate?.([{ x, y, tileType, action, playerId: this.player.id }]);
    }

    /**
     * Handle sword hold from client
     */
    sendPortalActivate(): void {
        this.player.wantsPortalActivation = true;
    }

    sendSwordHold(holding: boolean): void {
        if (holding && !this.player.isHoldingSword) {
            this.player.isHoldingSword = true;
            this.player.swordHoldStartTick = this.lastGlobalTick;
            // Don't reset runeStrikeTicks - cooldown persists across holds

            // Immediate sword tile check — a quick release could arrive
            // before the next tickCombat, skipping the sword section
            const events = this.simulation.checkSwordTile(this.player, this.lastGlobalTick);
            this.processSimulationEvents(events);
        } else if (!holding && this.player.isHoldingSword) {
            this.player.isHoldingSword = false;
        }
    }

    sendToolAction(action: string, playerX: number, playerY: number, facing: number, isCrouching: boolean): void {
        this.player.x = playerX;
        this.player.y = playerY;
        this.player.facing = facing;
        this.player.isCrouching = isCrouching;
        const events = this.simulation.processToolAction(
            this.player, action as ToolActionType, [this.player], this.lastGlobalTick,
        );
        this.processSimulationEvents(events);
    }

    /**
     * Air strafe (no-op for single player, just update position)
     */
    sendAirStrafe(dir: number): void {
        this.player.x += dir;
        this.player.facing = dir;
    }

    /**
     * Send speech message (in offline mode, triggers callback for local player)
     */
    sendSpeech(words: number[], voice: string = 'pona'): void {
        // In offline mode, immediately show speech bubble for local player
        this.onSpeech?.(this.player.id, words, voice);

        // Check for rodent spawn phrases: o pali soweli <type> (4 glyphs)
        if (words.length === 4) {
            if (words.every((w, i) => w === MOUSE_SPAWN_PHRASE[i])) {
                this.spawnRodentForPlayer(RodentType.MOUSE);
            } else if (words.every((w, i) => w === RAT_SPAWN_PHRASE[i])) {
                this.spawnRodentForPlayer(RodentType.RAT);
            } else if (words.every((w, i) => w === SQUIRREL_SPAWN_PHRASE[i])) {
                this.spawnRodentForPlayer(RodentType.SQUIRREL);
            } else if (words.every((w, i) => w === CRAB_SPAWN_PHRASE[i])) {
                this.spawnRodentForPlayer(RodentType.CRAB);
            } else {
                // New creature spawn: "o pali <category> <modifier>"
                const creatureId = matchCreatureSpawn(words);
                if (creatureId) {
                    this.spawnRodentForPlayer(creatureId);
                }
            }
        }

        // Check for block spawn phrases: o pali kiwen <block-name>
        for (const cmd of BLOCK_SPAWN_COMMANDS) {
            if (words.length === cmd.phrase.length && words.every((w, i) => w === cmd.phrase[i])) {
                this.player.isHoldingBlock = true;
                this.player.heldBlockType = cmd.blockType;
                this.onBlockSpawn?.(cmd.blockType);
                break;
            }
        }

        // Check for jump height commands: o suli/lili sewi <amount>
        for (const cmd of JUMP_HEIGHT_COMMANDS) {
            if (words.length === cmd.phrase.length && words.every((w, i) => w === cmd.phrase[i])) {
                this.player.jumpHeight = Math.max(0, this.player.jumpHeight + cmd.delta);
                this.onJumpHeightUpdate?.(this.player.jumpHeight);
                break;
            }
        }

        // Check for tree spawn: o kasi
        if (words.length === TREE_SPAWN_PHRASE.length && words.every((w, i) => w === TREE_SPAWN_PHRASE[i])) {
            this.spawnTreeForPlayer();
        }

        // Command block speech storage: if player is standing on KIWEN_LI, store the speech
        const height = this.player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        const feetY = this.player.y + height - 1;
        const belowTile = this.worldData.getTile(this.player.x, feetY + 1);
        if (belowTile === TileType.KIWEN_LI) {
            this.worldData.setCommandBlockSpeech(this.player.x, feetY + 1, words, voice);
        }
    }

    /**
     * Cycle sign glyph (KIWEN_NIMI interaction)
     */
    sendSignCycle(x: number, y: number): void {
        this.worldData.cycleSignGlyph(x, y);
        const glyphIndex = this.worldData.getSignGlyph(x, y);
        this.onSignUpdate?.(x, y, glyphIndex);
    }

    /**
     * Set spawn point (KIWEN_MI interaction)
     */
    sendSetSpawnPoint(x: number, y: number): void {
        this.player.spawnBlockX = x;
        this.player.spawnBlockY = y - 1;
        this.onSpawnPointSet?.(x, y - 1);
    }

    // ============================================================
    // TICK PROCESSING
    // ============================================================

    private tickFallingBlocks(): void {
        // Collect all falling tiles from all loaded chunks
        const fallingTiles: { worldX: number; worldY: number; tile: TileType }[] = [];

        for (const [key, chunk] of this.worldData.chunks) {
            const [cx, cy] = key.split(',').map(Number);
            for (let y = 0; y < CHUNK_SIZE; y++) {
                for (let x = 0; x < CHUNK_SIZE; x++) {
                    const tile = chunk[y][x];
                    if (tile === TileType.DIRT || tile === TileType.GRASS || tile === TileType.SKULL || tile === TileType.LOG || tile === TileType.WOODEN_SHIELD || tile === TileType.STONE_CLUB || tile === TileType.IRON_SWORD
                        || tile === TileType.KILI_AKESI || tile === TileType.KILI_ALE || tile === TileType.KILI_IKE || tile === TileType.KILI_JELO || tile === TileType.KILI_KEN || tile === TileType.KILI_KIWEN || tile === TileType.KILI_KULUPU || tile === TileType.KILI_LAWA || tile === TileType.KILI_SOWELI
                        || isSandTile(tile)) {
                        fallingTiles.push({
                            worldX: cx * CHUNK_SIZE + x,
                            worldY: cy * CHUNK_SIZE + y,
                            tile
                        });
                    }
                }
            }
        }

        // Sort by Y descending (bottom = highest Y first) for unified processing
        fallingTiles.sort((a, b) => b.worldY - a.worldY);

        // Single unified pass: each tile falls, rolls, or stays
        for (const { worldX, worldY, tile } of fallingTiles) {
            // Re-check tile in case a previous iteration changed it
            if (this.worldData.getTile(worldX, worldY) !== tile) continue;

            const belowTile = this.worldData.getTile(worldX, worldY + 1);

            // --- VERTICAL FALL ---
            if (belowTile === TileType.SKY) {
                if (tile === TileType.LOG) {
                    const oldKey = `${worldX},${worldY}`;
                    const inertia = this.logInertia.get(oldKey);
                    this.logInertia.delete(oldKey);
                    if (inertia !== undefined) {
                        this.logInertia.set(`${worldX},${worldY + 1}`, inertia);
                    }
                }
                this.worldData.setTile(worldX, worldY, TileType.SKY);
                this.worldData.setTile(worldX, worldY + 1, tile);
                continue;
            }

            // --- LOG ROLLING: on stable ground, try diagonal roll ---
            if (tile === TileType.LOG) {
                let dir = this.logInertia.get(`${worldX},${worldY}`);
                if (dir === undefined) {
                    dir = (worldY % 2 === 0) ? 1 : -1;
                }

                for (const tryDir of [dir, -dir]) {
                    const diagX = worldX + tryDir;
                    const diagY = worldY + 1;
                    if (this.worldData.getTile(diagX, worldY) === TileType.SKY &&
                        this.worldData.getTile(diagX, diagY) === TileType.SKY) {
                        this.logInertia.delete(`${worldX},${worldY}`);
                        this.worldData.setTile(worldX, worldY, TileType.SKY);
                        this.worldData.setTile(diagX, diagY, TileType.LOG);
                        this.logInertia.set(`${diagX},${diagY}`, tryDir);
                        break;
                    }
                }
            }
        }
    }

    private tickCombat(currentTick: number): void {
        const events = this.simulation.tickCombat([this.player], currentTick);
        this.processSimulationEvents(events);
    }

    private tickClubMelee(currentTick: number): void {
        const events = this.simulation.tickClubMelee([this.player], currentTick);
        this.processSimulationEvents(events);
    }

    private tickPortals(): void {
        const events = this.simulation.tickPortals([this.player]);
        this.processSimulationEvents(events);
    }

    private tickBreathing(): void {
        const prevAir = this.player.airTicks;
        const getTile = (x: number, y: number) => this.worldData.getTile(x, y);
        const events = this.simulation.tickBreathing([this.player], getTile);
        this.processSimulationEvents(events);
        if (this.player.airTicks !== prevAir) {
            this.onAirUpdate?.(this.player.airTicks);
        }
    }

    private tickBoots(): void {
        const prevJump = this.player.jumpHeight;
        const getTile = (x: number, y: number) => this.worldData.getTile(x, y);
        const events = this.simulation.tickBoots([this.player], getTile);
        this.processSimulationEvents(events);
        if (this.player.jumpHeight !== prevJump) {
            this.player.hasBoots = true;
            this.onJumpHeightUpdate?.(this.player.jumpHeight);
        }
    }

    private tickYellowKey(): void {
        const getTile = (x: number, y: number) => this.worldData.getTile(x, y);
        const events = this.simulation.tickYellowKey([this.player], getTile);
        if (events.length > 0) {
            this.processSimulationEvents(events);
            this.onYellowKeyPickup?.();
        }
    }

    private tickPressurePlates(): void {
        const events = this.simulation.tickPressurePlates([this.player]);
        this.processSimulationEvents(events);
    }

    private tickCommandBlocks(currentTick: number): void {
        const events = this.simulation.tickCommandBlocks([this.player], currentTick);
        this.processSimulationEvents(events);
    }

    private tickThwomps(): void {
        const events = this.simulation.tickThwomps([this.player]);
        this.processSimulationEvents(events);
    }

    private tickQuestionBlocks(currentTick: number): void {
        const events = this.simulation.tickQuestionBlocks([this.player], currentTick);
        this.processSimulationEvents(events);
    }

    private tickThornbushes(): void {
        const getTile = (x: number, y: number) => this.worldData.getTile(x, y);
        const events = this.simulation.tickThornbushes([this.player], getTile);
        this.processSimulationEvents(events);
    }

    private tickShrubSupport(): void {
        const events = this.simulation.tickShrubSupport();
        this.processSimulationEvents(events);
    }

    private tickFruitHazards(): void {
        const getTile = (x: number, y: number) => this.worldData.getTile(x, y);
        const events = this.simulation.tickFruitHazards([this.player], getTile);
        this.processSimulationEvents(events);
    }

    private tickSlowHeal(): void {
        const events = this.simulation.tickSlowHeal([this.player]);
        this.processSimulationEvents(events);
    }

    private tickSuffocation(): void {
        const getTile = (x: number, y: number) => this.worldData.getTile(x, y);
        const events = this.simulation.tickSuffocation([this.player], getTile);
        this.processSimulationEvents(events);
    }

    private processSimulationEvents(events: SimulationEvent[]): void {
        // Batch tile changes so the client receives tree destruction atomically
        const tileChanges: { x: number; y: number; tileType: TileType; action: string }[] = [];

        for (const event of events) {
            switch (event.type) {
                case 'slashSpawned':
                    this.onSlash?.(event.slash);
                    break;
                case 'runeUpdated':
                    this.onRuneUpdate?.(event.x, event.y, event.hieroglyphIndex);
                    break;
                case 'tileChanged':
                    tileChanges.push({ x: event.x, y: event.y, tileType: event.tileType, action: 'place' });
                    break;
                case 'damage':
                    this.onDamage?.(event.playerId, event.hp, event.source);
                    break;
                case 'death':
                    this.handleDeath(event.deathX, event.deathY);
                    break;
                case 'teleport':
                    console.log(`[PORTAL] Player on portal [${event.portalCode.join(',')}] -> ${event.destination.worldId}`);
                    this.onTeleport?.(event.destination, event.portalCode);
                    break;
                case 'pressurePlateChanged':
                    this.onPressurePlateChanged?.(event.x, event.y, event.active);
                    break;
                case 'commandBlockSpeech':
                    this.onSpeech?.(`block_${event.x}_${event.y}`, event.words, event.voice);
                    break;
                case 'signUpdated':
                    this.onSignUpdate?.(event.x, event.y, event.glyphIndex);
                    break;
                case 'spawnPointSet':
                    this.onSpawnPointSet?.(event.x, event.y);
                    break;
                case 'eyeSwitchActivated':
                    this.onEyeSwitchActivated?.(event.x, event.y, event.tick);
                    break;
            }
        }

        if (tileChanges.length > 0) {
            this.onBlockUpdate?.(tileChanges);
        }
    }

    // ============================================================
    // MOUSE ENEMIES
    // ============================================================

    private spawnRodentForPlayer(rodentType: string = RodentType.MOUSE): void {
        const playerHeight = this.player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        const size = getCreatureSize(rodentType);

        let spawnX: number;
        let spawnY: number;
        if (size.width > 1 || size.height > 1) {
            // Multi-tile: place bottom-left in front of player at ground level
            spawnY = this.player.y + playerHeight - 1;
            if (this.player.facing >= 0) {
                spawnX = this.player.x + 1;
            } else {
                spawnX = this.player.x - size.width;
            }
        } else {
            spawnX = this.player.x + this.player.facing;
            spawnY = this.player.y + playerHeight - 1;
        }

        const mouse = this.mouseSimulation.spawnMouse(spawnX, spawnY, this.player.facing, rodentType);
        this.onMouseSpawn?.(mouse.id, mouse.x, mouse.y, mouse.facing, mouse.rodentType, mouse.wallDir, mouse.vertDir);
    }

    private spawnTreeForPlayer(): void {
        const height = this.player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        const centerX = this.player.x + this.player.facing;
        const baseY = this.player.y + height - 1;

        const tileChanges: { x: number; y: number; tileType: TileType; action: string }[] = [];
        for (const block of buildTreeBlocks(centerX, baseY)) {
            if (this.worldData.getTile(block.x, block.y) === TileType.SKY) {
                this.worldData.setTile(block.x, block.y, block.tileType);
                tileChanges.push({ x: block.x, y: block.y, tileType: block.tileType, action: 'place' });
            }
        }
        if (tileChanges.length > 0) {
            this.onBlockUpdate?.(tileChanges);
        }
    }

    /** Spawn a tree at a specific world position (used when skull is placed on sapling) */
    private spawnTreeAtPosition(centerX: number, baseY: number): void {
        const tileChanges: { x: number; y: number; tileType: TileType; action: string }[] = [];
        for (const block of buildTreeBlocks(centerX, baseY)) {
            if (this.worldData.getTile(block.x, block.y) === TileType.SKY) {
                this.worldData.setTile(block.x, block.y, block.tileType);
                tileChanges.push({ x: block.x, y: block.y, tileType: block.tileType, action: 'place' });
            }
        }
        if (tileChanges.length > 0) {
            this.onBlockUpdate?.(tileChanges);
        }
    }

    private tickMice(): void {
        const getTile = (x: number, y: number) => this.worldData.getTile(x, y);

        // Suffocation first (block landed on mouse)
        const suffocated = this.mouseSimulation.tickSuffocation(getTile);
        this.processMouseEvents(suffocated);

        // Movement (only grounded mice), then falling
        const absorptions = this.mouseSimulation.tickMovement(getTile);
        for (const abs of absorptions) {
            this.worldData.addNestOccupant(abs.nestX, abs.nestY, {
                rodentType: abs.rodentType,
                timer: RATS_NEST_TIMER,
                facing: abs.facing,
                wallDir: abs.wallDir,
                vertDir: abs.vertDir,
            });
            this.onMouseDeath?.(abs.mouseId);
        }
        this.mouseSimulation.tickFalling(getTile);

        const events = this.mouseSimulation.tickCombat([this.player], this.simulation.activeSlashes);
        this.processMouseEvents(events);
    }

    private tickNests(): void {
        const respawns = this.worldData.tickNests();
        const getTile = (x: number, y: number) => this.worldData.getTile(x, y);

        for (const resp of respawns) {
            // Check which side of the nest is open, prefer entry side
            const spawn = computeNestRespawnPosition(resp, getTile);
            if (!spawn) {
                // Both sides blocked — re-queue in nest
                this.worldData.addNestOccupant(resp.nestX, resp.nestY, {
                    rodentType: resp.rodentType,
                    timer: RATS_NEST_TIMER,
                    facing: resp.facing,
                    wallDir: resp.wallDir,
                    vertDir: resp.vertDir,
                });
                continue;
            }

            const mouse = this.mouseSimulation.spawnMouse(spawn.x, spawn.y, spawn.facing, resp.rodentType);
            if (resp.rodentType === RodentType.SQUIRREL) {
                mouse.wallDir = spawn.wallDir ?? 0;
                mouse.vertDir = spawn.vertDir ?? 1;
            }
            this.onMouseSpawn?.(mouse.id, spawn.x, spawn.y, spawn.facing, resp.rodentType,
                mouse.wallDir, mouse.vertDir);
        }
    }

    private processMouseEvents(events: MouseEvent[]): void {
        for (const event of events) {
            switch (event.type) {
                case 'mouseDied':
                    this.onMouseDeath?.(event.mouseId);
                    break;
                case 'mousePlayerDamage':
                    this.onDamage?.(event.playerId, event.hp, 'mouse');
                    break;
                case 'mousePlayerDeath':
                    this.handleDeath(event.deathX, event.deathY);
                    break;
            }
        }
    }

    private handleDeath(deathX: number, deathY: number): void {
        this.player.airTicks = MAX_AIR_TICKS;
        this.player.drowningTickCounter = 0;

        // Place skull tile at death location (falls like dirt) — mirrors GameRoom.handleDeath
        const currentTile = this.worldData.getTile(deathX, deathY);
        if (currentTile === TileType.SKY) {
            this.worldData.setTile(deathX, deathY, TileType.SKULL);
            this.onBlockUpdate?.([{
                x: deathX,
                y: deathY,
                tileType: TileType.SKULL,
                action: 'place',
            }]);
        }

        // Custom respawn at KIWEN_MI block
        if (this.player.spawnBlockX >= 0) {
            this.player.hp = this.player.maxHp;
            this.player.x = this.player.spawnBlockX;
            this.player.y = this.player.spawnBlockY;
            this.player.state = PlayerState.FALLING;
            this.onRespawnAt?.(this.player.spawnBlockX, this.player.spawnBlockY);
            return;
        }

        this.onDeath?.(this.player.id);
    }

    // ============================================================
    // PERSISTENCE (for local saves)
    // ============================================================

    /**
     * Get the world data for saving
     */
    getWorldData(): WorldData {
        return this.worldData;
    }

    /**
     * Load world data
     */
    loadWorldData(worldData: WorldData): void {
        this.worldData = worldData;
        this.seed = worldData.seed;
        this.simulation.worldData = worldData;
    }

    /**
     * Set player's state (for preserving across teleports)
     */
    setPlayerState(isHoldingBlock: boolean, heldBlockType: number, facing: number, jumpHeight?: number): void {
        this.player.isHoldingBlock = isHoldingBlock;
        this.player.heldBlockType = heldBlockType;
        this.player.facing = facing;
        if (jumpHeight !== undefined) {
            this.player.jumpHeight = jumpHeight;
        }
    }

    /**
     * Set player's position (for portal return teleport)
     */
    setPlayerPosition(x: number, y: number): void {
        this.player.x = x;
        this.player.y = y;
    }
}
